import type { KeyboardEvent } from "react";
import { Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

type ServiceCardProps = {
  name: string;
  subsectorName: string | null;
  categoryId: string;
  isSelected: boolean;
  onToggle: () => void;
  onInfoClick: () => void;
};

function ServiceCard({
  name,
  subsectorName,
  categoryId,
  isSelected,
  onToggle,
  onInfoClick,
}: ServiceCardProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      className={cn(
        "flex items-start gap-3 p-3 rounded-xl border text-left transition-all duration-200 w-full",
        isSelected
          ? "border-ds-blue-400 bg-ds-blue-50/40 shadow-sm"
          : "border-ds-border-200 hover:border-ds-border-300 hover:bg-ds-surface-50/50"
      )}
    >
      <Checkbox
        checked={isSelected}
        className="mt-0.5 pointer-events-none"
        tabIndex={-1}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-navy-900 leading-tight">
          {name}
        </p>
        {subsectorName && (
          <p className="text-xs text-ds-text-500 mt-0.5">{subsectorName}</p>
        )}
        <Badge
          variant={categoryId === "1" ? "default" : "secondary"}
          className="mt-1.5 text-xs"
        >
          {categoryId === "1" ? "Anexa I" : "Anexa II"}
        </Badge>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0 w-7 h-7"
        onClick={(event) => {
          event.stopPropagation();
          onInfoClick();
        }}
      >
        <Info className="w-3.5 h-3.5 text-ds-text-400" />
      </Button>
    </div>
  );
}

export default ServiceCard;
